import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Input } from '../../components/ui/Input';
import { Logo } from '../../components/marca/Logo';
import { useSesionStore } from '../../stores/sesionStore';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { env, STORAGE_PREFIX } from '../../../config/env';
import estilos from './Login.module.css';

type EstadoLimpieza = 'inicial' | 'limpiando' | 'hecho' | 'error';

const TEXTO_LIMPIEZA: Record<EstadoLimpieza, string> = {
  inicial: 'Limpiar sesiones con problemas',
  limpiando: 'Limpiando…',
  hecho: 'Sesiones limpiadas',
  error: 'No se pudo limpiar',
};

export function Login() {
  const acceder = useSesionStore((s) => s.acceder);
  const accederComoDemostracion = useSesionStore((s) => s.accederComoDemostracion);
  const navegar = useNavigate();
  const ubicacion = useLocation();

  // Si llegó aquí por intentar abrir una sección concreta, se le devuelve a ella.
  const destino = (ubicacion.state as { destino?: string } | null)?.destino ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recordar, setRecordar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limpieza, setLimpieza] = useState<EstadoLimpieza>('inicial');
  const [entrandoDemo, setEntrandoDemo] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);

    try {
      await acceder({ email, password, recordar });
      navegar(destino, { replace: true });
    } catch (e) {
      registrarError('login', e);
      setError(
        e instanceof ErrorApp ? e.message : 'No se pudo iniciar sesión. Inténtalo de nuevo.',
      );
    } finally {
      setEnviando(false);
    }
  };

  /** Entra con la cuenta de muestra; las credenciales las guarda el servidor. */
  const entrarEnDemostracion = async () => {
    setError(null);
    setEntrandoDemo(true);

    try {
      await accederComoDemostracion();
      navegar(destino, { replace: true });
    } catch (e) {
      registrarError('login.demostracion', e);
      setError(
        e instanceof ErrorApp ? e.message : 'No se pudo abrir la demostración. Inténtalo de nuevo.',
      );
    } finally {
      setEntrandoDemo(false);
    }
  };

  /*
   * Enlace directo a la demostración: /?demo
   *
   * Sirve para enseñar la consola sin dar credenciales ni explicar dónde
   * pulsar. Se ejecuta una sola vez —de ahí la referencia— porque si fallara
   * y el efecto volviera a dispararse quedaría reintentando en bucle contra el
   * servidor.
   */
  const yaIntentado = useRef(false);

  useEffect(() => {
    if (yaIntentado.current) return;
    if (!new URLSearchParams(ubicacion.search).has('demo')) return;

    yaIntentado.current = true;
    void entrarEnDemostracion();
    // Sólo depende de la dirección con la que se entró.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacion.search]);

  /**
   * Descarta la sesión guardada cuando queda en un estado del que no se puede
   * salir iniciando sesión de nuevo. Sólo borra lo que guarda esta aplicación.
   */
  const limpiarSesiones = () => {
    setLimpieza('limpiando');
    try {
      for (const almacen of [localStorage, sessionStorage]) {
        for (const clave of Object.keys(almacen)) {
          if (clave.startsWith(STORAGE_PREFIX)) almacen.removeItem(clave);
        }
      }
      setLimpieza('hecho');
    } catch (e) {
      registrarError('login.limpiarSesiones', e);
      setLimpieza('error');
    }
    setTimeout(() => setLimpieza('inicial'), 2000);
  };

  return (
    <div className={estilos.contenedor}>
      <div className={estilos.caja}>
        <div className={estilos.encabezado}>
          <Logo variante="centrado" alto={80} className={estilos.logo} />
          <h1 className={estilos.titulo}>{env.marca.nombreApp}</h1>
        </div>

        <form className={estilos.formulario} onSubmit={enviar} noValidate>
          {error && (
            <div className={estilos.aviso} role="alert">
              <Icon nombre="errorContorno" tamano={16} />
              <span>{error}</span>
            </div>
          )}

          <Input
            etiqueta="Correo"
            type="email"
            placeholder="nombre@empresa.com"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            etiqueta="Contraseña"
            alternarVisibilidad
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <div className={estilos.opciones}>
            <label className={estilos.recordar}>
              <input
                type="checkbox"
                checked={recordar}
                onChange={(e) => setRecordar(e.target.checked)}
              />
              Mantener sesión abierta
            </label>
            <Link className={estilos.enlace} to="/recuperar-clave">
              ¿Olvidaste tu clave?
            </Link>
          </div>

          <Button type="submit" anchoCompleto cargando={enviando}>
            {enviando ? 'Verificando…' : 'Iniciar sesión'}
          </Button>

          <Button
            variante="secundario"
            anchoCompleto
            icono="ver"
            cargando={entrandoDemo}
            onClick={() => void entrarEnDemostracion()}
          >
            Ver una demostración
          </Button>

          <Button
            variante="secundario"
            anchoCompleto
            onClick={limpiarSesiones}
            cargando={limpieza === 'limpiando'}
            icono={limpieza === 'hecho' ? 'exito' : undefined}
          >
            {TEXTO_LIMPIEZA[limpieza]}
          </Button>

          <p className={estilos.pie}>powered by Omsaica</p>
        </form>
      </div>
    </div>
  );
}
